import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import DropdownProduct from '@components/Header/DropdownProduct';
import DropdownShowcase from '@components/Header/DropdownShowcase';
import DropdownResources from '@components/Header/DropdownResources';
import DropdownCompany from '@components/Header/DropdownCompany';
import IconLeHamburger from '@images/icons/IconLeHamburger/IconLeHamburger';
import Link from '@components/Link/Link';
import css from './Header.module.scss';
import SymbolSharetribe from '@images/logos/SymbolSharetribe/SymbolSharetribe';
import Modal from '@components/Modal/Modal';
import MobileMenu from '@components/MobileMenu/MobileMenu';
import { m, AnimatePresence } from 'framer-motion';
import { TNS } from '../../config';
import LogoSharetribe from '../../images/logos/LogoSharetribe/LogoSharetribe';
import constructURLWithQueryParams from '@utils/constructURLWithQueryParams';
import useLocalStorageValues from '@utils/useLocalStorageValues';
import PrismicLink from '../PrismicLink/PrismicLink';

const Header = ({ location, mode = 'light' }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);
  const [tnsURL, setTnsURL] = useState(TNS.START_BUILDING_URL);
  const { rfsn, referrer, firstPageVisited } = useLocalStorageValues();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
  };
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Change header background color when scrolling
  // sets isScrolled to true and class is added conditionally in header element
  useEffect(() => {
    const onScroll = () => {
      const scrollPosition = window.scrollY;
      if (headerRef.current) {
        if (scrollPosition > 50) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      }
    };

    onScroll();

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setTnsURL(
      constructURLWithQueryParams(TNS.START_BUILDING_URL, rfsn, referrer, firstPageVisited)
    );
  }, [rfsn, referrer, firstPageVisited]);

  const isRenderingRoute = (pathnames, location, exact) => {
    const isInBrowser = typeof window !== 'undefined';
    const currentPathname = location
      ? location.pathname
      : isInBrowser
      ? window.location.pathname
      : null;

    // Function to check if a single pathname matches the current pathname
    const matchesPathname = pathname => {
      return currentPathname && exact
        ? currentPathname === pathname
        : currentPathname && !exact
        ? currentPathname.indexOf(pathname) >= 0
        : false;
    };

    if (Array.isArray(pathnames)) {
      // If pathnames is an array, check each pathname in the array
      for (const pathname of pathnames) {
        if (matchesPathname(pathname)) {
          return true; // Return true if any pathname in the array matches
        }
      }
    } else {
      // If pathnames is a single pathname, check it directly
      return matchesPathname(pathnames);
    }

    return false; // No match found
  };

  const isRenderingPricingPage = isRenderingRoute('/pricing/', location, false);
  const isRenderingShowcasePage = isRenderingRoute('/customers/', location, false);
  const isRenderingProductPage = isRenderingRoute(
    [
      '/features/',
      '/rental-marketplace/',
      '/service-marketplace/',
      '/product-marketplace/',
      '/any-marketplace/',
    ],
    location,
    false
  );
  const isRenderingResourcesPage = isRenderingRoute(
    ['/academy/', '/twosided/', '/developers/', '/experts/', '/developer-blog/'],
    location,
    false
  );
  const isRenderingCompanyPage = isRenderingRoute(['/balanced/', '/company/'], location, false);

  // Close dropdown when clicking outside of it
  useEffect(() => {
    const closeDropdownOnOutsideClick = e => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !e.target.classList.contains(css.desktopLink) &&
        activeDropdown
      ) {
        setActiveDropdown(null);
      }
    };

    // Close dropdown when pressing ESC
    const closeDropdownOnEsc = e => {
      if (e.key === 'Escape' && activeDropdown) {
        setActiveDropdown(null);
      }
    };

    // Add both event listeners
    window.addEventListener('click', closeDropdownOnOutsideClick);
    window.addEventListener('keydown', closeDropdownOnEsc);

    // Cleanup both event listeners when the component is unmounted
    return () => {
      window.removeEventListener('click', closeDropdownOnOutsideClick);
      window.removeEventListener('keydown', closeDropdownOnEsc);
    };
  }, [activeDropdown]);

  const toggleDropdown = item => {
    // Check if a dropdown is currently open
    const isDropdownOpen = Boolean(activeDropdown);

    // If a dropdown is open and the clicked item is different from the current activeDropdown
    if (isDropdownOpen && activeDropdown !== item) {
      setActiveDropdown(item);
    } else {
      setActiveDropdown(activeDropdown === item ? null : item);
    }
  };

  // Dropdown component
  const DropdownContent = ({ activeDropdown, name, dropdownVariants, children }) => {
    return (
      activeDropdown === name && (
        <m.div variants={dropdownVariants} initial="hidden" animate="visible" exit="exit">
          {children}
        </m.div>
      )
    );
  };

  // Animations
  // Animation variants for the dropdown menu
  const dropdownVariants = {
    // duration = 0 since the alert banner is on in order to avoid flicker
    hidden: {
      opacity: 0,
      transition: { duration: 0.0, ease: 'easeInOut' },
    },
    visible: {
      opacity: 1,
      transition: { duration: 0.0, ease: 'easeInOut' },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0, ease: 'easeInOut' },
    },
  };

  // Animation variants for the background overlay
  const overlayVariants = {
    // duration = 0 since the alert banner is on in order to avoid flicker
    hidden: { opacity: 0, transition: { duration: 0, ease: 'easeInOut' } },
    visible: { opacity: 1, transition: { duration: 0, ease: 'easeInOut' } },
    exit: { opacity: 0, transition: { duration: 0, ease: 'easeInOut' } },
  };

  return (
    <header
      ref={headerRef}
      className={classNames(css.header, {
        [css.scrolled]: isScrolled,
        [css.withActiveDropdown]: activeDropdown,
        [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
      })}
    >
      <div className={css.sharetribeLogosDesktop}>
        <LogoSharetribe mode={activeDropdown || isScrolled ? 'light' : mode} />
      </div>

      <div className={css.sharetribeLogosMobile}>
        <SymbolSharetribe mode={activeDropdown || isScrolled ? 'light' : mode} />
      </div>

      <nav className={css.navDesktop}>
        <ul className={css.desktopMenu}>
          <button
            className={classNames(css.desktopLink, {
              [css.activeLink]: activeDropdown === 'Product',
              [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
              [css.active]: isRenderingProductPage,
            })}
            onClick={() => toggleDropdown('Product')}
          >
            Product
            <span
              className={classNames(css.arrow, { [css.rotated]: activeDropdown === 'Product' })}
            ></span>
          </button>
          <Link
            className={classNames(css.desktopLink, {
              [css.active]: isRenderingPricingPage,
              [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
            })}
            to="/pricing/"
          >
            Pricing
          </Link>
          <button
            className={classNames(css.desktopLink, {
              [css.activeLink]: activeDropdown === 'Showcase',
              [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
              [css.active]: isRenderingShowcasePage,
            })}
            onClick={() => toggleDropdown('Showcase')}
          >
            Showcase
            <span
              className={classNames(css.arrow, {
                [css.rotated]: activeDropdown === 'Showcase',
                [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
              })}
            ></span>
          </button>
          <button
            className={classNames(css.desktopLink, {
              [css.activeLink]: activeDropdown === 'Resources',
              [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
              [css.active]: isRenderingResourcesPage,
            })}
            onClick={() => toggleDropdown('Resources')}
          >
            Resources & Support
            <span
              className={classNames(css.arrow, { [css.rotated]: activeDropdown === 'Resources' })}
            ></span>
          </button>
          <button
            className={classNames(css.desktopLink, {
              [css.activeLink]: activeDropdown === 'Company',
              [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
              [css.active]: isRenderingCompanyPage,
            })}
            onClick={() => toggleDropdown('Company')}
          >
            Company
            <span
              className={classNames(css.arrow, { [css.rotated]: activeDropdown === 'Company' })}
            ></span>
          </button>
        </ul>
      </nav>

      <nav className={css.navMobile}>
        <PrismicLink
          to={tnsURL}
          analyticsEvent="custom-sharetribe-account-started"
          analyticsProperties={{ position: 'header--main' }}
          className={css.startBuildingCTAMobile}
        >
          Start free trial
        </PrismicLink>
        <button
          className={css.mobileMenuButton}
          onClick={openMobileMenu}
          aria-label="Open Sharetribe's mobile navigation"
        >
          <IconLeHamburger mode={activeDropdown || isScrolled ? 'light' : mode} />
        </button>
      </nav>

      <Modal contentLabel="Mobile menu" isOpen={isMobileMenuOpen} onClose={closeMobileMenu}>
        <MobileMenu />
      </Modal>

      <AnimatePresence>
        {activeDropdown && (
          <m.div
            className={css.overlay}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <m.div
              ref={dropdownRef}
              className={css.dropdownContent}
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <hr />
              <DropdownContent
                activeDropdown={activeDropdown}
                name="Product"
                overlayVariants={overlayVariants}
              >
                <DropdownProduct setActiveDropdown={setActiveDropdown} />
              </DropdownContent>

              <DropdownContent
                activeDropdown={activeDropdown}
                name="Showcase"
                overlayVariants={overlayVariants}
              >
                <DropdownShowcase setActiveDropdown={setActiveDropdown} />
              </DropdownContent>

              <DropdownContent
                activeDropdown={activeDropdown}
                name="Resources"
                overlayVariants={overlayVariants}
              >
                <DropdownResources setActiveDropdown={setActiveDropdown} />
              </DropdownContent>

              <DropdownContent
                activeDropdown={activeDropdown}
                name="Company"
                overlayVariants={overlayVariants}
              >
                <DropdownCompany setActiveDropdown={setActiveDropdown} />
              </DropdownContent>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>

      <div className={css.navCTA}>
        <Link
          className={classNames(css.loginLink, {
            [css.darkMode]: !(activeDropdown || isScrolled) && mode === 'dark',
          })}
          to={TNS.LOGIN_URL}
        >
          Log in
        </Link>
        <PrismicLink
          to={tnsURL}
          analyticsEvent="custom-sharetribe-account-started"
          analyticsProperties={{ position: 'header--main' }}
          className={css.startBuildingCTA}
        >
          Start free trial
        </PrismicLink>
      </div>
    </header>
  );
};

export default Header;
